import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Link2 } from 'lucide-react';
import { Code, MagicWand } from '@phosphor-icons/react';
import { Umschalter } from './bereich/Formular';

// Wiederverwendbarer HTML-Editor mit Umschalter Visuell/HTML + schwebender Format-Toolbar.
// value/onChange sind HTML. Beim Laden wird das HTML mit DOMPurify gesäubert (gespeichertes XSS).
// Hinweis: Der Editor initialisiert seinen Inhalt aus `value` beim Mounten und beim Wechsel auf
// 'visuell'. Wechselt der Aufrufer den bearbeiteten Datensatz, sollte er den Editor über eine
// `key`-Prop neu mounten.

interface RichHtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** Mobil: Umschalter 44 px hoch. */
  mobil?: boolean;
}

const clean = (html: string) =>
  DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true }, FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'] });

const RichHtmlEditor: React.FC<RichHtmlEditorProps> = ({ value, onChange, placeholder = 'Text schreiben…', minHeight = 180, mobil = false }) => {
  const [modus, setModus] = useState<'visuell' | 'html'>('visuell');
  const [toolbar, setToolbar] = useState<{ top: number; left: number } | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modus === 'visuell' && editorRef.current) editorRef.current.innerHTML = clean(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modus]);

  const updateToolbar = useCallback(() => {
    const sel = window.getSelection();
    const box = editorRef.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !box) { setToolbar(null); return; }
    const range = sel.getRangeAt(0);
    if (!box.contains(range.commonAncestorContainer)) { setToolbar(null); return; }
    const rect = range.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) { setToolbar(null); return; }
    setToolbar({ top: rect.top - b.top - 46, left: Math.min(Math.max(rect.left - b.left + rect.width / 2, 90), b.width - 90) });
  }, []);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    if (editorRef.current) onChange(editorRef.current.innerHTML);
    updateToolbar();
  };
  const einfuegenLink = () => {
    const url = window.prompt('Link-URL (mit https://):', 'https://');
    if (url && url !== 'https://') exec('createLink', url);
  };

  const toolBtn = 'rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors';

  return (
    <div>
      <div className="flex justify-end" style={{ marginBottom: 6 }}>
        <Umschalter hoehe={mobil ? 44 : 28} wert={modus} onChange={setModus}
          optionen={[{ key: 'visuell', label: 'Visuell', icon: <MagicWand size={12} weight="bold" /> }, { key: 'html', label: 'HTML', icon: <Code size={12} weight="bold" /> }]} />
      </div>

      {modus === 'visuell' ? (
        <div className="relative overflow-visible" style={{ borderRadius: 14, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
            onMouseUp={updateToolbar}
            onKeyUp={updateToolbar}
            onBlur={() => setTimeout(() => setToolbar(null), 150)}
            data-placeholder={placeholder}
            style={{ minHeight }}
            className="px-4 py-3 outline-none text-sm leading-relaxed text-gray-800 [&_a]:text-[#1a1d24] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-300 [&:empty]:before:pointer-events-none"
          />
          {toolbar && (
            <div className="absolute z-30 flex items-center gap-0.5 rounded-lg bg-[#1a1d24] border border-white/10 px-1 py-1 shadow-xl -translate-x-1/2" style={{ top: toolbar.top, left: toolbar.left }} onMouseDown={e => e.preventDefault()}>
              <button type="button" title="Fett" onClick={() => exec('bold')} className={toolBtn}><Bold size={14} /></button>
              <button type="button" title="Kursiv" onClick={() => exec('italic')} className={toolBtn}><Italic size={14} /></button>
              <button type="button" title="Unterstrichen" onClick={() => exec('underline')} className={toolBtn}><Underline size={14} /></button>
              <button type="button" title="Durchgestrichen" onClick={() => exec('strikeThrough')} className={toolBtn}><Strikethrough size={14} /></button>
              <span className="mx-0.5 h-4 w-px bg-white/15" />
              <button type="button" title="Aufzählung" onClick={() => exec('insertUnorderedList')} className={toolBtn}><List size={14} /></button>
              <button type="button" title="Nummerierte Liste" onClick={() => exec('insertOrderedList')} className={toolBtn}><ListOrdered size={14} /></button>
              <span className="mx-0.5 h-4 w-px bg-white/15" />
              <button type="button" title="Link einfügen" onClick={einfuegenLink} className={toolBtn}><Link2 size={14} /></button>
            </div>
          )}
        </div>
      ) : (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full px-4 py-3 rounded-[14px] text-[12px] font-mono outline-none resize-y"
          style={{ minHeight, background: 'var(--bg)', color: 'var(--ink)', border: 'none', boxShadow: 'inset 0 0 0 1px var(--line)' }}
          placeholder={placeholder}
        />
      )}
    </div>
  );
};

export default RichHtmlEditor;
